<template>
  <div class="holiday-builder" :class="{ 'fairness-only': fairnessExpanded }">
    <header class="banner">
      <div class="banner-titles">
        <h2>Holiday Schedule</h2>
        <p>Configure how the children spend each holiday, special day, and school break. The agreement on the right updates as you go.</p>
      </div>
      <div class="banner-actions">
        <button v-if="isPrimary" type="button" class="btn-secondary" @click="resetAll" title="Clear all rules">↺ Reset</button>
        <button type="button" class="btn-download" @click="printAgreement" title="Print or save this page as a PDF">↓ Download PDF</button>
      </div>
    </header>

    <!-- v-show — expanding Fairness check hides the split beneath it. -->
    <div v-show="!fairnessExpanded" class="layout">
      <!-- Left: editor -->
      <section class="editor">
        <!-- Toolbar: overall progress + discuss filter. -->
        <div class="editor-toolbar">
          <div class="overall-progress">
            <strong>{{ decidedCount }} of {{ totalCount }}</strong> decisions made
            <div class="overall-bar">
              <div class="overall-bar-fill" :style="{ width: progressPct + '%' }"></div>
            </div>
          </div>
          <button
            v-if="flaggedCount > 0"
            type="button"
            class="discuss-filter"
            :class="{ active: showOnlyFlagged }"
            :title="showOnlyFlagged ? 'Show all rows' : 'Show only flagged rows'"
            @click="showOnlyFlagged = !showOnlyFlagged"
          >
            <span class="discuss-flag" aria-hidden="true">⚑</span>
            {{ flaggedCount }} to discuss
            <span class="discuss-action">{{ showOnlyFlagged ? '· Show all' : '· Show only' }}</span>
          </button>
        </div>

        <!-- data-category-key — toggleCategory's scroll anchoring looks the element up by it. -->
        <div v-for="cat in groupedHolidays" :key="cat.key" class="category" :class="{ collapsed: !isCategoryOpen(cat.key) }" :data-category-key="cat.key">
          <button
            type="button"
            class="category-head"
            :aria-expanded="isCategoryOpen(cat.key) ? 'true' : 'false'"
            :aria-controls="`cat-body-${cat.key}`"
            @click="toggleCategory(cat.key)"
          >
            <span class="cat-chevron" aria-hidden="true">▾</span>
            <span class="cat-label">{{ cat.label }}</span>
            <span class="cat-progress" :class="{ done: cat.decided === cat.list.length && cat.flagged === 0 }">
              {{ cat.decided }} of {{ cat.list.length }} decided<template v-if="cat.flagged > 0"> · {{ cat.flagged }} to discuss</template>
            </span>
          </button>

          <div
            :id="`cat-body-${cat.key}`"
            v-show="isCategoryOpen(cat.key)"
            class="category-body"
            role="region"
            :aria-label="cat.label"
          >
            <details v-if="isPrimary && templatesFor(cat.key).length" class="cat-templates">
              <summary>
                <span class="cat-templates-icon" aria-hidden="true">⚡</span>
                Quick-start templates for {{ cat.label.toLowerCase() }}
              </summary>
              <HolidayTemplateCards
                :templates="templatesFor(cat.key)"
                @apply="(payload) => applyTemplate(cat.key, payload.key, { reverse: payload.reverse })"
              />
            </details>

            <HolidayRow
              v-for="h in cat.list"
              :key="h.id"
              :holiday="h"
              :partyAName="partyAName"
              :partyBName="partyBName"
              :jurisdiction="jurisdiction"
              :role="role"
              :currentUserName="currentUserName"
              :open="openRows.indexOf(h.id) !== -1"
              @update="updateHoliday"
              @remove="removeHoliday"
              @toggle="toggleRow"
              @apply-to-category="applyRowToCategory"
            />

            <!-- Add custom item for this category (primary only) -->
            <template v-if="isPrimary">
              <div v-if="addingCategory === cat.key" class="add-custom-form">
                <input
                  :ref="`add-${cat.key}`"
                  v-model="newName"
                  type="text"
                  :placeholder="addPlaceholder(cat.key)"
                  maxlength="60"
                  @keyup.enter="confirmAddCustom(cat.key)"
                  @keyup.escape="cancelAdd"
                />
                <button type="button" class="btn-add" @click="confirmAddCustom(cat.key)">Add</button>
                <button type="button" class="btn-cancel" @click="cancelAdd">Cancel</button>
              </div>
              <button v-else type="button" class="add-custom-btn" @click="startAdd(cat.key)">
                + Add {{ addLabel(cat.key) }}
              </button>
            </template>
          </div>
        </div>
      </section>

      <!-- Right: tabbed live preview. Whatever tab is active is what prints. -->
      <aside class="preview">
        <nav class="preview-tabs">
          <button
            v-for="t in TABS"
            :key="t.value"
            type="button"
            :class="['tab', { active: previewTab === t.value }]"
            @click="previewTab = t.value"
          >{{ t.label }}</button>
          <select
            v-if="previewTab === 'year'"
            class="year-select"
            :value="selectedYear"
            @change="selectedYear = Number($event.target.value)"
          >
            <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
          </select>
        </nav>

        <!-- All three preview panes always live in the DOM. On screen, CSS
             shows only the one matching `previewTab`; when printing, CSS
             reveals all three with page breaks between so the PDF contains
             every view in one file. -->
        <div class="preview-stack" :class="`active-${previewTab}`">
          <AgreementPreview
            class="preview-pane preview-agreement"
            :holidays="holidays"
            :partyAName="partyAName"
            :partyBName="partyBName"
            :jurisdiction="jurisdiction"
            :children="children"
          />
          <AgreementTable
            class="preview-pane preview-overall"
            :holidays="holidays"
            :partyAName="partyAName"
            :partyBName="partyBName"
            :jurisdiction="jurisdiction"
            :children="children"
          />
          <YearTable
            class="preview-pane preview-year"
            :holidays="holidays"
            :partyAName="partyAName"
            :partyBName="partyBName"
            :jurisdiction="jurisdiction"
            :children="children"
            :year="selectedYear"
          />
          <CalendarView
            class="preview-pane preview-calendar"
            :rotation="rotation"
            :holidays="holidays"
            :jurisdiction="jurisdiction"
            :partyAName="partyAName"
            :partyBName="partyBName"
          />
        </div>
      </aside>
    </div>

    <section class="rotation-block">
      <FairnessDashboard
        :holidays="holidays"
        :partyAName="partyAName"
        :partyBName="partyBName"
        :children="children"
        :jurisdiction="jurisdiction"
        @toggle-open="onFairnessToggle"
      />
    </section>
  </div>
</template>

<script>
import HolidayRow from './components/HolidayRow.vue'
import AgreementPreview from './components/AgreementPreview.vue'
import AgreementTable from './components/AgreementTable.vue'
import YearTable from './components/YearTable.vue'
import FairnessDashboard from './components/FairnessDashboard.vue'
import HolidayTemplateCards from './components/HolidayTemplateCards.vue'
import CalendarView from '../CalendarView.vue'
import { CATEGORIES } from '@/lib/parentingSchedule/seed/holidays'
import { partyFileTitle } from '@/lib/agreement/filters'
import { lastNameOf } from '@/lib/parentingSchedule/children'
import { load, save, clear, flushSave } from './holidayDraft.js'
import dialogs from '../dialogs.js'
import { TEMPLATES_BY_CATEGORY } from '@/lib/parentingSchedule/holidayTemplates.js'
// Accordion scroll anchoring — see toggleCategory / toggleRow.
import {
  getScrollContainer,
  readScrollTop,
  writeScrollTop,
  compensateScrollForCollapseAbove,
  measureCollapseAbove,
} from './scrollContainer.js'

const CATEGORY_ORDER = ['holiday', 'special-day', 'school-break', 'christmas']

const TABS = [
  { value: 'agreement', label: 'Agreement' },
  { value: 'overall',   label: 'At a glance' },
  { value: 'year',      label: 'Year view' },
  { value: 'calendar',  label: 'Calendar' },
]

// Flip every A↔B parent reference in a rule. Used by the per-category
// "Swap parents" button to mirror an arrangement (e.g. start the
// chronological alternation with the other parent).
const SWAP_FIELDS = ['parent', 'evenParent', 'firstHalfParent', 'startParent']
function swapAB(p) {
  if (p === 'A') return 'B'
  if (p === 'B') return 'A'
  return p
}
function swapRule(rule) {
  if (!rule) return rule
  const next = { ...rule }
  for (const field of SWAP_FIELDS) {
    if (next[field] === 'A' || next[field] === 'B') next[field] = swapAB(next[field])
  }
  return next
}

const NEEDS_DECISION_KINDS = new Set(['unset', 'agreement-required'])
function isUnset(h) {
  return NEEDS_DECISION_KINDS.has(h.rule.kind)
}

// "Apply to all" must carry the whole arrangement, not just the
// rule. Copying `rule` alone silently wipes the pickup/dropoff times and notes
// already entered on every target row.
const ARRANGEMENT_COPY_FIELDS = ['pickup', 'dropoff', 'timesByAgreement', 'customProse', 'notes']

function copyArrangementFields(source) {
  const patch = { rule: { ...source.rule } }
  for (const field of ARRANGEMENT_COPY_FIELDS) {
    patch[field] = source[field]
  }
  return patch
}

export default {
  name: 'HolidayBuilder',
  components: { HolidayRow, AgreementPreview, AgreementTable, YearTable, FairnessDashboard, HolidayTemplateCards, CalendarView },
  mixins: [dialogs], // $confirmDialog / $alertDialog
  props: {
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    partyAPronouns: { type: String, default: '' },
    partyBPronouns: { type: String, default: '' },
    jurisdiction: { type: String, default: 'BC' },
    caseId: { type: String, default: '' },
    children: { type: Array, default: () => [] },
    // Permission role. 'primary' (default) can edit rules, add/remove custom
    // rows, run templates, and reset. 'secondary' is a comment-only role that
    // can toggle the discuss flag and write a discussionNote per holiday but
    // cannot modify the rule, times, or membership of the list. Wire this
    // from Divii's case permissions on a per-user basis.
    role: {
      type: String,
      default: 'primary',
      validator: function (v) { return v === 'primary' || v === 'secondary' || v === 'viewer' },
    },
    // Display name of the user currently signed in on this session. Stamped
    // onto every discussionNote this session writes so the other party can
    // see "Note from Gwyneth" instead of a generic label. Divii should pass
    // its authenticated user's display name (e.g. currentUser.displayName).
    currentUserName: { type: String, default: '' },
    // The Parenting Time rotation for the same case (the parenting app's
    // rotation document). Powers the Calendar preview tab, which paints the
    // whole year with rotation days + holiday-decision overrides. In Divii,
    // fetch the sibling cases/{caseId}/parentingSchedule document.
    rotation: { type: Object, default: null },
  },
  data() {
    return {
      holidays: [],
      previewTab: 'agreement',
      selectedYear: new Date().getFullYear(),
      TABS,
      addingCategory: null,
      newName: '',
      // Accordion + per-row open state. Plain arrays (Vue 2's reactivity
      // doesn't track Set / Map mutations, so Sets won't trigger re-render).
      openCategories: [],
      openRows: [],
      // When true, the editor only renders rows that have flaggedForDiscussion: true.
      // Toggled from the toolbar's "X to discuss · Show only" chip.
      showOnlyFlagged: false,
      // When fairness is expanded, hide the editor+preview split.
      fairnessExpanded: false,
    }
  },
  computed: {
    isPrimary() { return this.role === 'primary' },
    isSecondary() { return this.role === 'secondary' },
    anyConfigured() {
      return this.holidays.some((h) => !NEEDS_DECISION_KINDS.has(h.rule.kind))
    },
    groupedHolidays() {
      // When the discuss filter is active, narrow the source list before
      // grouping so categories with zero matches drop out entirely.
      const source = this.showOnlyFlagged
        ? this.holidays.filter((h) => h.flaggedForDiscussion)
        : this.holidays
      const byCategory = {}
      for (const h of source) (byCategory[h.category] ||= []).push(h)
      return CATEGORY_ORDER
        .filter((k) => byCategory[k]?.length)
        .map((k) => {
          const list = byCategory[k]
          const decided = list.filter((h) => !isUnset(h)).length
          const flagged = list.filter((h) => h.flaggedForDiscussion).length
          return { key: k, label: CATEGORIES[k], list, decided, flagged }
        })
    },
    decidedCount() {
      return this.holidays.filter((h) => !isUnset(h)).length
    },
    totalCount() {
      return this.holidays.length
    },
    flaggedCount() {
      return this.holidays.filter((h) => h.flaggedForDiscussion).length
    },
    progressPct() {
      if (this.totalCount === 0) return 0
      return Math.round((this.decidedCount / this.totalCount) * 100)
    },
    availableYears() {
      const start = new Date().getFullYear()
      // Offer current year + next 4
      return [start, start + 1, start + 2, start + 3, start + 4]
    },
  },
  watch: {
    holidays: {
      deep: true,
      handler(v) { save(this.caseId, v) },
    },
    // Refresh seeded names if the parent app passes new party names (e.g. user
    // edits them in the wider Divii case). We only rewrite *unmodified* names
    // — the birthday rows that contain the party-name placeholder.
    partyAName() { this.refreshPlaceholderNames() },
    partyBName() { this.refreshPlaceholderNames() },
    // Same idea for the children's-birthday row: when there's a single child
    // with a name we use "{Child}'s Birthday" instead of "Children's Birthday".
    children: {
      deep: true,
      handler() { this.refreshPlaceholderNames() },
    },
    // If the user un-flags the last flagged row while the filter is on, the
    // filter chip disappears but the editor would still be filtered to an
    // empty set. Auto-clear the filter so they see all rows again.
    flaggedCount(n) {
      if (n === 0 && this.showOnlyFlagged) this.showOnlyFlagged = false
    },
    // Reload if the agreement changes without a remount.
    caseId: {
      async handler(nextId, prevId) {
        if (!nextId || nextId === prevId) return
        this.holidays = await load(nextId, this.partyAName, this.partyBName, this.jurisdiction)
        this.refreshPlaceholderNames()
        this.openCategories = []
        this.openRows = []
      },
    },
  },
  // The Firestore-backed store debounces and suppresses its own write echoes,
  // so the load is a plain fire-and-forget await (and beforeDestroy flushes).
  created() {
    this.initHolidays()
  },
  beforeDestroy() {
    flushSave()
  },
  methods: {
    isCategoryOpen(key) {
      return this.openCategories.indexOf(key) !== -1
    },
    // Single-open accordions inside a scroll container shift the
    // page when the previously-open block sits above the one you clicked, so
    // the thing you clicked slides out from under the cursor. Both toggles
    // record the scroll/anchor position first and restore it after the DOM
    // settles. See scrollContainer.js.
    toggleCategory(key) {
      // Single-open accordion: clicking a different category closes whichever
      // was open and focuses the new one. Clicking the currently-open
      // category collapses it.
      const catEl = this.$el.querySelector(`[data-category-key="${key}"]`)
      const scroller = getScrollContainer(catEl) || document.documentElement
      const wasOpen = this.openCategories.indexOf(key) !== -1
      let collapseDelta = 0

      if (!wasOpen) {
        const prevKey = this.openCategories[0]
        if (prevKey && prevKey !== key) {
          const prevEl = this.$el.querySelector(`[data-category-key="${prevKey}"]`)
          const prevBody = prevEl && prevEl.querySelector('.category-body')
          collapseDelta = measureCollapseAbove(prevEl, catEl, prevBody)
        }
      }

      const scrollTop = readScrollTop(scroller)
      this.openCategories = wasOpen ? [] : [key]

      if (!wasOpen && collapseDelta > 0) {
        this.$nextTick(() => {
          compensateScrollForCollapseAbove(scroller, scrollTop, collapseDelta)
        })
      }
    },
    toggleRow(id) {
      // Single-open accordion behavior at the row level too.
      const rowEl = this.$el.querySelector(`[data-holiday-id="${id}"]`)
      const scroller = getScrollContainer(rowEl) || document.documentElement
      const wasOpen = this.openRows.indexOf(id) !== -1
      const anchorEl = rowEl && (rowEl.querySelector('.row-head') || rowEl)
      const anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null
      const anchorScrollTop = readScrollTop(scroller)

      this.openRows = wasOpen ? [] : [id]

      if (!wasOpen && anchorEl && anchorTop != null) {
        // Re-pin across three frames: Vue's patch, then layout, then any
        // late reflow from the expanded body's own content.
        const keepAnchor = () => {
          const delta = anchorEl.getBoundingClientRect().top - anchorTop
          if (Math.abs(delta) < 1) return
          writeScrollTop(scroller, Math.max(0, anchorScrollTop + delta))
        }
        this.$nextTick(() => {
          keepAnchor()
          requestAnimationFrame(() => {
            keepAnchor()
            requestAnimationFrame(keepAnchor)
          })
        })
      }
    },
    updateHoliday({ id, patch }) {
      const idx = this.holidays.findIndex((h) => h.id === id)
      if (idx === -1) return
      const before = this.holidays[idx]
      this.holidays.splice(idx, 1, { ...before, ...patch })

      // Anchoring Winter Break to the Christmas half makes the Christmas &
      // New Year days part of the break's arrangement — reflect that in
      // their rows so the section reads as decided (rows the user already
      // decided are left alone; a decided day still out-ranks the break).
      if (id === 'winter-break' && patch.rule) {
        const hadAnchor = !!before.rule?.christmasWeek
        const hasAnchor = !!patch.rule.christmasWeek
        const XMAS_IDS = ['christmas-eve', 'christmas-day', 'new-years-eve', 'new-years-day']
        if (!hadAnchor && hasAnchor) {
          const updated = []
          this.holidays = this.holidays.map((h) => {
            const undecided = !h.rule?.kind || h.rule.kind === 'unset' || h.rule.kind === 'agreement-required'
            if (XMAS_IDS.includes(h.id) && undecided) {
              updated.push(h.name)
              return { ...h, rule: { kind: 'follows-break' } }
            }
            return h
          })
          const skipped = XMAS_IDS.length - updated.length
          if (updated.length) {
            this.$alertDialog(`${updated.join(', ')} now follow the Winter Break arrangement.`
              + (skipped > 0 ? ' Days you had already decided were left unchanged; a decided day still overrides the break.' : ''),
              { title: 'Winter Break applied' })
          }
        } else if (hadAnchor && !hasAnchor) {
          // Un-anchoring: release only the rows the anchor set itself.
          this.holidays = this.holidays.map((h) =>
            XMAS_IDS.includes(h.id) && h.rule?.kind === 'follows-break'
              ? { ...h, rule: { kind: 'unset' } }
              : h)
        }
      }
    },
    // Copy one row's rule onto every other row in the same category. Confirmed
    // with the user before applying since it overwrites their other choices.
    // Like a quick-start template but carrying a specific configured arrangement.
    async applyRowToCategory(sourceId) {
      const source = this.holidays.find((h) => h.id === sourceId)
      if (!source) return
      const cat = source.category
      const others = this.holidays.filter((h) => h.category === cat && h.id !== sourceId)
      if (!others.length) return
      const catLabel = CATEGORIES[cat] || 'this section'
      const msg = `Apply this arrangement to all ${others.length} other ${catLabel}? This will overwrite their current rules.`
      const proceed = await this.$confirmDialog(msg, { title: 'Apply to section', okTitle: 'Apply to all' })
      if (!proceed) return
      this.holidays = this.holidays.map((h) => {
        if (h.category !== cat || h.id === sourceId) return h
        return { ...h, ...copyArrangementFields(source) } // whole arrangement, not just the rule
      })
    },
    async removeHoliday(id) {
      const h = this.holidays.find((h) => h.id === id)
      if (!h) return
      const proceed = await this.$confirmDialog(`Remove ${h.name} from the schedule?`, {
        title: 'Remove day',
        okTitle: 'Remove',
      })
      if (!proceed) return
      this.holidays = this.holidays.filter((h) => h.id !== id)
      // Also clean the row out of openRows so it doesn't leak.
      if (this.openRows.indexOf(id) !== -1) {
        this.openRows = this.openRows.filter((rowId) => rowId !== id)
      }
    },
    startAdd(catKey) {
      this.addingCategory = catKey
      this.newName = ''
      this.$nextTick(() => {
        // Known dead: `:ref` inside a v-for gives an ARRAY in Vue 2, so `el.focus`
        // is undefined and this autofocus has never fired. Left as-is deliberately
        // — making it work is a UX change, not a refactor. Ticketed separately.
        const el = this.$refs[`add-${catKey}`]
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    cancelAdd() {
      this.addingCategory = null
      this.newName = ''
    },
    confirmAddCustom(catKey) {
      const name = this.newName.trim()
      if (!name) return
      const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      this.holidays.push({
        id,
        category: catKey,
        name,
        longWeekendType: null,
        rule: { kind: 'unset' },
        pickup: '',
        dropoff: '',
        timesByAgreement: false,
        customProse: '',
        notes: '',
        whyThisMatters: '',
        flaggedForDiscussion: false,
        discussionNote: null,
        customName: false,
      })
      // New custom rows start with the editor open so the user can
      // configure the rule right away. The category will already be open
      // (you had to expand it to click "+ Add").
      this.openRows = this.openRows.concat(id)
      this.cancelAdd()
    },
    addLabel(catKey) {
      const map = {
        'holiday':       'a holiday',
        'special-day':   'a special day',
        'school-break':  'a school break',
        'christmas':     'another day',
      }
      return map[catKey] || 'item'
    },
    addPlaceholder(catKey) {
      const map = {
        'holiday':       'e.g. Diwali, Eid, National Indigenous Peoples Day',
        'special-day':   'e.g. First day of school, anniversary',
        'school-break':  'e.g. Mid-winter break',
        'christmas':     'e.g. Boxing Day, Hanukkah',
      }
      return map[catKey] || 'Name'
    },
    applyTemplate(categoryKey, templateKey, options = {}) {
      const list = TEMPLATES_BY_CATEGORY[categoryKey] || []
      const t = list.find((x) => x.key === templateKey)
      if (!t) return
      const ctx = {
        partyAPronouns: this.partyAPronouns,
        partyBPronouns: this.partyBPronouns,
      }
      this.holidays = this.holidays.map((h) => {
        if (h.category !== categoryKey) return h
        const next = t.apply(h, ctx)
        // Reverse only flips parent assignments on rules the template just produced.
        // It's a no-op for non-reversible rule shapes (tie-to-weekend, share, etc.).
        return options.reverse ? { ...next, rule: swapRule(next.rule) } : next
      })

      // "Each parent's day with that parent" assigns Mother's/Father's Day by
      // pronoun match. When the pronouns are missing or ambiguous, say so
      // instead of silently leaving those rows undecided.
      if (templateKey === 'parent-days') {
        const sheMatch = (ctx.partyAPronouns === 'she/her') !== (ctx.partyBPronouns === 'she/her')
        const heMatch = (ctx.partyAPronouns === 'he/him') !== (ctx.partyBPronouns === 'he/him')
        const stillUnset = (id) => {
          const h = this.holidays.find((x) => x.id === id)
          return h && (!h.rule?.kind || h.rule.kind === 'unset' || h.rule.kind === 'agreement-required')
        }
        const left = []
        if (!sheMatch && stillUnset('mothers-day')) left.push("Mother's Day")
        if (!heMatch && stillUnset('fathers-day')) left.push("Father's Day")
        if (left.length) {
          const reason = (!ctx.partyAPronouns && !ctx.partyBPronouns)
            ? 'the parties’ pronouns are not set'
            : 'the parties’ pronouns do not give a unique match'
          this.$alertDialog(
            `${left.join(' and ')} stayed at "Needs decision" because ${reason}. Assign ${left.length > 1 ? 'them' : 'it'} manually, or set the pronouns and re-apply the template. Birthdays and the children's birthday were assigned.`,
            { title: 'Template partly applied' }
          )
        }
      }
    },
    onFairnessToggle(isOpen) {
      this.fairnessExpanded = !!isOpen
    },
    templatesFor(categoryKey) {
      return TEMPLATES_BY_CATEGORY[categoryKey] || []
    },
    async initHolidays() {
      this.holidays = await load(this.caseId, this.partyAName, this.partyBName, this.jurisdiction)
      // Apply the placeholder substitution once on load — gives the
      // children's-birthday row its correct name based on the children prop
      // before the user sees anything.
      this.refreshPlaceholderNames()
      // Start with everything collapsed so the page reads as a clean overview
      // (4 category bars + the toolbar). The user expands what they want to
      // work on; "Expand all" in the toolbar is the one-click escape hatch.
      this.openCategories = []
      this.openRows = []
    },
    async resetAll() {
      const proceed = await this.$confirmDialog(
        'Clear every rule and start over? Every holiday decision you have made will be reset to "Needs decision".',
        { title: 'Reset holiday schedule', okTitle: 'Clear all rules' }
      )
      if (!proceed) return
      this.holidays = await clear(this.caseId, this.partyAName, this.partyBName, this.jurisdiction)
      // Match the page-load default: everything collapsed.
      this.openCategories = []
      this.openRows = []
    },
    // The browser's save-as-PDF dialog suggests document.title as the
    // filename, so set it to "[LastName][LastName] Holiday Schedule" for the
    // duration of the print call (window.print blocks until the dialog
    // closes), then restore the app title.
    printAgreement() {
      const prev = document.title
      document.title = partyFileTitle(lastNameOf(this.partyAName), lastNameOf(this.partyBName), 'Holiday Schedule')
      // The host page carries the app-chrome print rules gated on
      // this body class — see ParentingSchedulePanel.vue. afterprint rather
      // than finally: window.print() returns early in some browsers.
      document.body.classList.add('parenting-schedule-printing')
      const cleanup = () => {
        document.title = prev
        document.body.classList.remove('parenting-schedule-printing')
        window.removeEventListener('afterprint', cleanup)
      }
      window.addEventListener('afterprint', cleanup)
      window.print()
    },
    refreshPlaceholderNames() {
      const A = this.partyAName || 'Party A'
      const B = this.partyBName || 'Party B'
      const childBirthdayName = this.computeChildBirthdayName()
      this.holidays = this.holidays.map((h) => {
        // Respect the user's manual rename — once customName is true, we
        // stop auto-substituting party names into this row.
        if (h.customName) return h
        if (h.id === 'party-a-birthday') return { ...h, name: `${A}'s Birthday` }
        if (h.id === 'party-b-birthday') return { ...h, name: `${B}'s Birthday` }
        if (h.id === 'childrens-birthday') return { ...h, name: childBirthdayName }
        return h
      })
    },
    // When there's exactly one child with a name, use "Sasha's Birthday"
    // instead of the generic "Children's Birthday". Falls back to the
    // generic when there are 0 or 2+ children, or when the single child
    // has no name yet.
    computeChildBirthdayName() {
      if (Array.isArray(this.children) && this.children.length === 1) {
        const name = this.children[0]?.name?.trim()
        if (name) return `${name}'s Birthday`
      }
      return "Children's Birthday"
    },
  },
}
</script>

<style scoped>
/* The builder is embedded in a fixed-height tab. Without this chain
   (builder → layout → editor/preview) the whole panel scrolls as one block and
   carries the banner and the hub's tab strip off-screen. */
.holiday-builder {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  min-height: 0;
}

/* Divii-style dark-teal section banner (matches the Support Calculator pattern) */
.banner {
  background: #34555f;
  color: #fff;
  border-radius: 10px;
  padding: 0.95rem 1.15rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.banner-titles h2 {
  margin: 0 0 0.1rem;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.banner-titles p {
  margin: 0;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.78);
  max-width: 60ch;
}
.banner-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}
.btn-secondary {
  font-family: inherit;
  font-size: 0.82rem;
  padding: 0.45rem 0.9rem;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 500;
}
/* The one button every tab shares: gold Download PDF, rightmost in the
   banner here and in ParentingBuilder's banner. Keep in sync. */
.btn-download {
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.45rem 0.9rem;
  border-radius: 6px;
  cursor: pointer;
  background: #f7a91b;
  border: 1px solid #f7a91b;
  color: #1e293b;
}
.btn-download:hover { background: #d8911a; border-color: #d8911a; }
.btn-secondary {
  background: transparent;
  color: #fff;
  border-color: rgba(255,255,255,0.4);
}
.btn-secondary:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.6); }

/* Editor toolbar: overall progress indicator + bulk expand/collapse links. */
.editor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.65rem 0.9rem;
}
.overall-progress {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.82rem;
  color: #475569;
  flex-grow: 1;
  min-width: 0;
}
.overall-progress strong {
  color: #1e293b;
  font-weight: 600;
}
.overall-bar {
  flex-grow: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
  max-width: 200px;
}
.overall-bar-fill {
  height: 100%;
  background: #3f6772;
  transition: width 0.25s ease;
}
/* Discuss filter chip — only appears when at least one row is flagged.
   Default state: amber outline (informational). Active state: solid amber
   (filtering on). Click toggles whether the editor narrows to flagged rows. */
.discuss-filter {
  font-family: inherit;
  font-size: 0.78rem;
  font-weight: 500;
  background: #fef6e0;
  color: #85581a;
  border: 1px solid #dc6b1c;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.discuss-filter:hover {
  border-color: #d8911a;
  background: #fde9bf;
}
.discuss-filter.active {
  background: #f7a91b;
  border-color: #d8911a;
  color: #fff;
}
.discuss-filter.active:hover {
  background: #d8911a;
}
.discuss-filter .discuss-flag { font-size: 0.85rem; }
.discuss-filter .discuss-action { color: inherit; opacity: 0.85; }

/* Per-category quick-start templates collapsible (one per category in the
   editor). Treated as a brand-gold call-to-action so it reads as "click
   me to skip ahead" rather than blending into the surrounding category. */
.cat-templates {
  background: #fef6e0;
  border: 1px solid #fde9bf;
  border-left: 3px solid #f7a91b;
  border-radius: 8px;
  padding: 0.55rem 0.95rem;
  margin-bottom: 0.6rem;
  transition: background 0.15s, border-color 0.15s;
}
.cat-templates:hover {
  background: #fde9bf;
}
.cat-templates summary {
  cursor: pointer;
  font-size: 0.86rem;
  color: #85581a;
  font-weight: 600;
  user-select: none;
  padding: 0.2rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.cat-templates-icon {
  font-size: 1rem;
  line-height: 1;
}
.cat-templates[open] summary {
  margin-bottom: 0.55rem;
  border-bottom: 1px dashed #fde9bf;
  padding-bottom: 0.5rem;
}
.cat-templates[open]:hover {
  background: #fef6e0;
}

.layout {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}
@media (max-width: 1000px) {
  .layout { grid-template-columns: 1fr; }
}

/* The editor column owns its own scrollbar. overflow-anchor:none
   stops the browser's scroll anchoring from fighting toggleRow's re-pinning. */
.editor {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-height: 0;
  overflow-y: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.5rem;
}
.preview {
  position: sticky;
  top: 0; /* the column scrolls, not the window */
  align-self: start;
  max-height: 100%;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  -webkit-overflow-scrolling: touch;
}
/* Single-column below 1000px — let the page scroll instead of
   trapping two short panes inside a squeezed grid. */
@media (max-width: 1000px) {
  .holiday-builder {
    height: auto;
    max-height: none;
    flex: none;
  }
  .editor,
  .preview {
    height: auto;
    max-height: calc(100vh - 280px);
  }
  .preview { top: 1rem; }
}

/* On screen: only the active preview pane is visible; the others stay
   mounted in the DOM (so their data is up-to-date) but are display:none.
   On print: all three become visible, with page breaks between so the
   downloaded PDF contains every view in one file. */
.preview-stack .preview-pane { display: none; }
.preview-stack.active-agreement .preview-agreement,
.preview-stack.active-overall .preview-overall,
.preview-stack.active-year .preview-year { display: block; }
/* CalendarView's root is a flex column — restore flex when it's the active pane. */
.preview-stack.active-calendar .preview-calendar { display: flex; }
@media print {
  .preview-stack .preview-pane { display: block !important; }
  .preview-stack .preview-calendar { display: flex !important; }
  .preview-stack .preview-pane + .preview-pane {
    page-break-before: always;
    break-before: page;
  }
}

/* Tabs in the preview pane */
.preview-tabs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.4rem 0.5rem;
}
.tab {
  font-family: inherit;
  background: transparent;
  border: 1px solid transparent;
  color: #475569;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.78rem;
  cursor: pointer;
  font-weight: 500;
}
.tab:hover { color: #34555f; }
.tab.active {
  background: #3f6772;
  color: #fff;
  border-color: #3f6772;
}
.year-select {
  margin-left: auto;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.3rem 0.55rem;
  font-size: 0.78rem;
  font-family: inherit;
}

/* Category accordion: clickable header that toggles a body of templates +
   rows + add-custom button. The chevron rotates on collapse. */
.category {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
}
.category-head {
  width: 100%;
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  padding: 0.35rem 0.25rem;
  border-radius: 6px;
  transition: background 0.15s;
}
.category-head:hover { background: rgba(58,100,112,0.06); }
.cat-chevron {
  color: #3f6772;
  font-size: 0.8rem;
  transition: transform 0.2s;
  display: inline-block;
}
.category.collapsed .cat-chevron { transform: rotate(-90deg); }
.cat-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #34555f;
  flex-grow: 1;
}
.cat-progress {
  font-size: 0.74rem;
  color: #64748b;
  font-weight: 500;
  background: #fff;
  border: 1px solid #e2e8f0;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  flex-shrink: 0;
}
.cat-progress.done {
  background: #d9e4e7;
  border-color: #a3bbc1;
  color: #244047;
}
.category-body {
  padding: 0.6rem 0.25rem 0.25rem;
  overflow-anchor: none; /* see .editor */
}

/* Add-custom button + inline form (per category) */
.add-custom-btn {
  margin-top: 0.55rem;
  background: transparent;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #557d89;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.add-custom-btn:hover {
  border-color: #3f6772;
  background: #eef3f4;
  color: #34555f;
}
.add-custom-form {
  margin-top: 0.55rem;
  display: flex;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid #3f6772;
  border-radius: 8px;
  padding: 0.45rem 0.55rem;
  align-items: center;
}
.add-custom-form input {
  flex-grow: 1;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #1e293b;
  padding: 0.35rem 0.55rem;
  font-size: 0.85rem;
  font-family: inherit;
  min-width: 0;
}
.add-custom-form input:focus {
  outline: none;
  border-color: #3f6772;
}
.btn-add,
.btn-cancel {
  font-family: inherit;
  font-size: 0.78rem;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 500;
  flex-shrink: 0;
}
.btn-add {
  background: #3f6772;
  color: #fff;
  border-color: #3f6772;
}
.btn-add:hover { background: #34555f; border-color: #34555f; }
.btn-cancel {
  background: #fff;
  color: #475569;
  border-color: #cbd5e1;
}
.btn-cancel:hover { border-color: #94a3b8; }

/* In fairness-only mode this block is the whole pane. */
.holiday-builder.fairness-only .rotation-block {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}
.rotation-block {
  margin-top: 0.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

/* Print: collapse the builder UI so only the .agreement-preview prints.
   We hide the banner, templates collapsible, editor column, and rotation
   table — leaving the preview to flow naturally on the printed page.
   The preview's own print rules (in AgreementPreview.vue) strip its border
   and padding so the prose fills the letter sheet cleanly. */
@media print {
  .holiday-builder { display: block; gap: 0; }
  .banner,
  .editor,
  .rotation-block,
  .preview-tabs { display: none !important; }
  .layout { display: block !important; gap: 0 !important; }
  .preview {
    position: static !important;
    max-height: none !important;
    overflow: visible !important;
    top: auto !important;
    display: block !important;
    gap: 0 !important;
  }
}
</style>
